/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';

const isFilled = (value: unknown): boolean =>
    typeof value === 'string' ? value.trim() !== '' : value !== undefined && value !== null && value !== '';

/**
 * Companion to `mode: 'onTouched'` forms. Validates every field that has a value — except the one the shopper is
 * typing in, until they have left it once — so errors show without having to click into each field:
 * - values pre-filled from the basket / profile / session show their errors as soon as the form appears
 *   (and again whenever the form is re-filled, e.g. by `form.reset` on Edit or address autocomplete),
 * - browser autofill (which fills fields without focusing them) shows its errors straight away, and
 * - every invalid filled field shows its error at the same time, not one by one as each is left.
 */
export function useRevalidateFilledFields<T extends FieldValues>(form: UseFormReturn<T>): void {
    useEffect(() => {
        const validateFilled = (values: Record<string, unknown>) => {
            const focusedName =
                typeof document !== 'undefined' ? (document.activeElement as HTMLInputElement | null)?.name : undefined;
            const toValidate = Object.keys(values).filter((key) => {
                if (!isFilled(values[key])) return false;
                // Don't flag the field being typed in until it has been left once (onTouched behaviour)
                return key !== focusedName || form.getFieldState(key as Path<T>).isTouched;
            }) as Path<T>[];
            if (toValidate.length) void form.trigger(toValidate);
        };

        // Pre-filled values: validate once the form is on screen
        validateFilled(form.getValues() as Record<string, unknown>);

        // Any change: typing, autofill, programmatic setValue (e.g. address autocomplete) or a reset / refill
        const subscription = form.watch((values) => validateFilled(values as Record<string, unknown>));
        return () => subscription.unsubscribe();
    }, [form]);
}
